import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { partnerAPI } from '@/services/PartnerAPI';
import type { Partner } from '@/types/partner';
import type { PartnerProduct } from '@/composables/services/usePartnerProductsAPI';
import { usePartnerAPI } from '@/composables/services/usePartnerAPI';
import { useUserStore } from '@/stores/user';

export const usePartnerStore = defineStore('partner', () => {
    const products = ref<PartnerProduct[]>([]);
    const partnerTeams = ref<any[]>([]);
    const currentPartner = ref<Partner | null>(null);
    const loading = ref(false);
    const loadingTeams = ref(false);
    const loadingCurrentPartner = ref(false);
    const error = ref<string | null>(null);
    const teamsError = ref<string | null>(null);
    const currentPartnerError = ref<string | null>(null);

    // In-flight promise deduplication — prevents concurrent callers from each
    // firing a separate network request before the first one resolves.
    let fetchProductsPromise: Promise<void> | null = null;
    let fetchPartnerTeamsPromise: Promise<void> | null = null;
    let fetchCurrentPartnerPromise: Promise<void> | null = null;

    // Helper computed properties for partner type checking
    const isServiceProvider = computed(() => {
        return currentPartner.value?.types?.includes('service_provider') ?? false;
    });

    const isStandardPartner = computed(() => {
        return currentPartner.value?.types?.includes('standard') ?? false;
    });

    function fetchProducts(): Promise<void> {
        if (products.value.length > 0) return Promise.resolve();
        if (fetchProductsPromise) return fetchProductsPromise;

        fetchProductsPromise = (async () => {
            try {
                loading.value = true;
                error.value = null;
                const { usePartnerProductsAPI } = await import('@/composables/services/usePartnerProductsAPI');
                const partnerProductsAPI = usePartnerProductsAPI();
                const results = await partnerProductsAPI.fetchPartnerProducts({ limit: 1000 });
                products.value = results?.products || [];
            } catch (err) {
                error.value = err instanceof Error ? err.message : 'Failed to fetch products';
                console.error('Error fetching products:', err);
                products.value = [];
            } finally {
                loading.value = false;
                fetchProductsPromise = null;
            }
        })();

        return fetchProductsPromise;
    }

    function fetchPartnerTeams(ecosystem: boolean = false): Promise<void> {
        if (partnerTeams.value.length > 0) return Promise.resolve();
        if (fetchPartnerTeamsPromise) return fetchPartnerTeamsPromise;

        fetchPartnerTeamsPromise = (async () => {
            try {
                loadingTeams.value = true;
                teamsError.value = null;

                const api = usePartnerAPI();
                const response = await api.fetchPartnerTeams(ecosystem);

                if (response.success) {
                    let teams = response.pl__org_partners_teams || [];

                    if (!ecosystem) {
                        const userStore = useUserStore();
                        const currentOrgId = userStore.userTeams?.[0]?.team_id?.substring(0, 3) || userStore.userTeams?.[0]?.substring(0, 3);
                        teams = teams.filter((team: any) => team.val.substring(0, 3) === currentOrgId);
                    }

                    partnerTeams.value = teams;
                } else {
                    throw new Error('Failed to fetch partner teams');
                }
            } catch (err) {
                teamsError.value = err instanceof Error ? err.message : 'Failed to fetch partner teams';
                console.error('Error fetching partner teams:', err);
            } finally {
                loadingTeams.value = false;
                fetchPartnerTeamsPromise = null;
            }
        })();

        return fetchPartnerTeamsPromise;
    }

    /**
     * Wait for user data to be available.
     * Checks synchronously first (fast path), then polls briefly to handle the
     * case where auth store populates userData inside a deferred setTimeout.
     * Max wait time: 20 attempts * 50ms = 1 second
     */
    async function waitForUserData(maxAttempts = 20, delayMs = 50): Promise<string> {
        const userStore = useUserStore();

        // Fast path — userData already available
        const immediate = userStore.userData?.object_id;
        if (immediate) return immediate;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
            const partnerId = userStore.userData?.object_id;
            if (partnerId) return partnerId;
        }

        throw new Error('No partner ID found in user data - user data not loaded after maximum wait time (1000ms)');
    }

    function fetchCurrentPartner(): Promise<void> {
        if (currentPartner.value) return Promise.resolve();
        if (fetchCurrentPartnerPromise) return fetchCurrentPartnerPromise;

        fetchCurrentPartnerPromise = (async () => {
            try {
                loadingCurrentPartner.value = true;
                currentPartnerError.value = null;

                const partnerId = await waitForUserData();

                const api = usePartnerAPI();
                const response = await api.fetchCurrentPartner(partnerId);

                if (response.success && response.partners.results.length > 0) {
                    currentPartner.value = response.partners.results[0];
                } else {
                    throw new Error('No partner data found');
                }
            } catch (err) {
                currentPartnerError.value = err instanceof Error ? err.message : 'Failed to fetch current partner';
                console.error('Error fetching current partner:', err);
            } finally {
                loadingCurrentPartner.value = false;
                fetchCurrentPartnerPromise = null;
            }
        })();

        return fetchCurrentPartnerPromise;
    }

    return {
        products,
        partnerTeams,
        currentPartner,
        loading,
        loadingTeams,
        loadingCurrentPartner,
        error,
        teamsError,
        currentPartnerError,
        isServiceProvider,
        isStandardPartner,
        fetchProducts,
        fetchPartnerTeams,
        fetchCurrentPartner
    };
}); 